import React, { useState } from "react";
import { Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Users, CheckCircle } from "lucide-react";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { trackOrganizationSignup } from "../lib/gtag";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  organizationName: "",
  organizationDescription: "",
};

const CoachSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, loading: authLoading, bootstrapSession } = useAuth();
  const [formData, setFormData] = useState(initialForm);

  // Capture referral ID from URL params (from referral email link)
  const referralRef = searchParams.get("ref");
  const [status, setStatus] = useState({
    loading: false,
    error: "",
    success: false,
    onboarding: null,
    organization: null,
    autoLogin: false,
  });

  const shouldRedirect = !authLoading && user && !status.success;

  if (shouldRedirect) {
    return <Navigate to="/productivity-coaching" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      // Include referral ref if present (for tracking referral signups)
      const payload = { ...formData };
      if (referralRef) {
        payload.referralRef = referralRef;
      }
      const { data } = await api.post("/auth/coach-signup", payload);
      let autoLogin = false;

      if (data?.token && data?.user) {
        bootstrapSession(data.token, data.user);
        autoLogin = true;
      } else {
        try {
          const loginResult = await login(formData.email, formData.password);
          autoLogin = Boolean(loginResult?.success);
        } catch (authError) {
          console.error("Auto login after signup failed", authError);
        }
      }

      // Track the organization signup event in Google Analytics
      trackOrganizationSignup({
        organizationId: data.organization?._id,
        organizationName: data.organization?.name,
        userId: data.user?._id,
      });

      setStatus({
        loading: false,
        error: "",
        success: true,
        onboarding: data.onboarding || null,
        organization: data.organization || null,
        autoLogin,
      });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "We couldn't create your workspace. Please double-check your details.";

      setStatus((prev) => ({ ...prev, loading: false, error: message }));
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Preparing your workspace...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#3A7DFF_0%,_transparent_50%)]" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-blue-200 mb-3">
            <Sparkles className="h-4 w-4" />
            Launch your HelloPC workspace
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white">
            Create your productivity coaching hub
          </h1>
          <p className="mt-4 text-lg text-slate-300">
            We'll spin up a dedicated organization for you and add you as the first coach.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <div className="bg-white rounded-3xl shadow-2xl shadow-blue-900/20 text-slate-900 p-8">
            {status.success ? (
              <div>
                <div className="flex items-center gap-3 text-blue-600 mb-6">
                  <CheckCircle className="h-6 w-6" />
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                      Workspace ready
                    </p>
                    <h2 className="text-2xl font-bold">
                      {status.organization?.name || "Your organization"} is live
                    </h2>
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed">
                  {status.onboarding?.message ||
                    "You're in! Start by adding the agents you coach so HelloPC can keep them on track."}
                </p>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold">
                      1
                    </span>
                    <div>
                      <p className="font-semibold text-slate-800">
                        Head to the Agents tab
                      </p>
                      <p className="text-slate-500">
                        Add your current roster so the dashboard can spotlight who needs attention.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold">
                      2
                    </span>
                    <div>
                      <p className="font-semibold text-slate-800">
                        Assign onboarding or accountability plans
                      </p>
                      <p className="text-slate-500">
                        Once agents are added you can assign plans so tasks, nudges, and reminders kick off.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={() => navigate("/agents")}
                    className="inline-flex justify-center items-center px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition"
                  >
                    Go add your agents
                  </button>
                  {!status.autoLogin && (
                    <p className="text-sm text-slate-500">
                      We couldn't sign you in automatically. Use your new credentials on the{" "}
                      <Link to="/login" className="text-blue-600 font-semibold">
                        login page
                      </Link>
                      .
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {status.error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                    {status.error}
                  </div>
                )}

                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      First name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Jordan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Last name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Lee"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    Work email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="coach@yourmarketcenter.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    minLength={6}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="At least 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    Organization or market center name
                  </label>
                  <input
                    type="text"
                    name="organizationName"
                    required
                    value={formData.organizationName}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Northside Productivity Coaching"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    What do you focus on? (optional)
                  </label>
                  <textarea
                    name="organizationDescription"
                    value={formData.organizationDescription}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="E.g. onboarding 5-10 agents per month, accountability coaching, launch series"
                    rows={3}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status.loading}
                  className="w-full inline-flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-white bg-blue-600 shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition disabled:opacity-70"
                >
                  {status.loading ? "Creating your workspace..." : "Create my HelloPC workspace"}
                </button>

                <p className="text-sm text-slate-500 text-center">
                  Already have an account?{" "}
                  <Link to="/login" className="text-blue-600 font-semibold">
                    Sign in here
                  </Link>
                </p>
              </form>
            )}
          </div>

          <div className="bg-white/10 border border-white/10 rounded-3xl p-6 text-white shadow-2xl shadow-blue-900/40">
            <div className="flex items-center gap-3 mb-4">
              <Users className="h-6 w-6 text-blue-200" />
              <p className="text-sm uppercase tracking-wide text-blue-100">
                Built for productivity coaches
              </p>
            </div>
            <ul className="space-y-5 text-sm text-blue-50">
              <li>
                <span className="font-semibold text-white">Add unlimited agents</span>
                <p className="text-blue-200 mt-1">
                  Keep onboarding cohorts, accountability partners, and launch series organized.
                </p>
              </li>
              <li>
                <span className="font-semibold text-white">Agent task plans</span>
                <p className="text-blue-200 mt-1">
                  Assign templates so every agent knows their next step the moment you add them.
                </p>
              </li>
              <li>
                <span className="font-semibold text-white">Momentum spotting</span>
                <p className="text-blue-200 mt-1">
                  Dashboards highlight who's slipping so you can reach out before production dips.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachSignup;
